import { splatLoadingAtom } from '@/atoms';
import { LoadingBadge, LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

/**
 * Bottom-left badge stack that surfaces splat-layer load progress while
 * `SplatMeshNode` is fetching/decoding. One row per layer; rows
 * disappear as soon as `SplatMesh.initialized` resolves. Failed loads
 * stick around so the user can see why nothing rendered.
 *
 * Sits as a sibling overlay inside the viewer's `relative` flex column
 * (see `ViewerPage.tsx`), with `pointer-events-none` so it never steals
 * interaction from the canvas.
 */
export const SplatLoadingOverlay = () => {
  const { t } = useTranslation('project');
  const loading = useAtomValue(splatLoadingAtom);
  const entries = Object.values(loading);
  if (entries.length === 0) return null;

  const labels = {
    fetching: t('splatLoader.fetching'),
    decoding: t('splatLoader.decoding'),
    failed: t('splatLoader.failed'),
  };

  return (
    <div className='pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-1.5'>
      {entries.map((e) => (
        <LoadingBadge
          key={e.layerId}
          name={e.layerName}
          phase={e.phase}
          progress={e.progress}
          loadedBytes={e.loadedBytes}
          totalBytes={e.totalBytes}
          error={e.error}
          labels={labels}
        />
      ))}
      <LoadingBadgeKeyframes />
    </div>
  );
};
